import * as Sentry from '@sentry/browser';

/**
 * Turns a host list response into reader models.
 *
 * Both steps live here so no caller can hydrate without validating first: the
 * response shape is checked, then each entry is rehydrated on its own.
 */

/**
 * Delfino types the host list responses as arrays and every caller maps over
 * them. A session that expires behind the iframe answers with a page instead,
 * so the shape is checked before it reaches a map that would break the load.
 */
const hostListOrEmpty = (response, listName) => {
    if (Array.isArray(response)) return response;

    const shape = response === null ? 'null' : typeof response;
    const message = `The host answered the ${listName} list with ${shape}, expected an array.`;

    // Sentry keeps every console argument as a breadcrumb on the next event, so the
    // response body never goes here: an expired session answers with a login page,
    // and a response can key entries by uuid. Its size is the diagnostic.
    console.error(message);
    Sentry.captureException(new Error(message), {
        tags: { feature: listName },
        extra: {
            keys: shape === 'object' ? Object.keys(response).length : 0,
            length: shape === 'string' ? response.length : 0,
        },
    });

    return [];
};

/**
 * Rehydration decrypts the reader's own text, so one unreadable entry must not
 * cost the rest of the list. The failures are reported once, by count.
 *
 * @param {*} response - What the host answered, trusted for nothing
 * @param {string} listName - The list being read, for the reports
 * @param {Function} toModel - Builds an unhydrated model from one host entry
 * @returns {Promise<Object[]>} the entries that rehydrated
 */
const hydrateHostList = async (response, listName, toModel) => {
    const items = hostListOrEmpty(response, listName).map(toModel);
    // The callback is async so an entry that arrived without reHydrate settles as a
    // rejection. A plain callback would throw out of the map, before allSettled runs,
    // and cost the whole list.
    const results = await Promise.allSettled(items.map(async item => item.reHydrate()));
    const failures = results.filter(result => result.status === 'rejected');

    if (failures.length > 0) {
        console.error(`${failures.length} of ${items.length} ${listName} failed to rehydrate`, failures[0].reason);
        Sentry.captureException(failures[0].reason, {
            tags: { feature: listName },
            extra: { list: listName, failed: failures.length, total: items.length },
        });
    }

    return results.filter(result => result.status === 'fulfilled').map(result => result.value);
};

export const hydrateHighlightsFromHost = (highlightModel, response) =>
    hydrateHostList(response, 'highlights', highlight => ({ ...highlightModel, ...highlight, confirmed: true }));

export const hydrateFlashcardsFromHost = (flashcardModel, response) =>
    hydrateHostList(response, 'flashcards', flashcard => ({ ...flashcardModel, ...flashcard }));
